import React, { useContext, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Seo from "./common/Seo";
import {
  getAnalytics,
  logEvent as logEventFirebase,
  setUserProperties,
} from "firebase/analytics";
import { firebaseApp } from "../store/LoggerContext";

import Top from "./FreeTrial/Top";
import Stepper from "./FreeTrial/Stepper";
import Video from "./FreeTrial/Video";
import WhatIs from "./FreeTrial/WhatIs";
import InstallApp from "./common/InstallApp/InstallApp";
import FAQs from "./FreeTrial/FAQs";
import Testimonials from "./FreeTrial/Testimonials";
import Trial from "./FreeTrial/Trial";
import { AccountContext } from "../store/AccountContext";
import { OverviewContext } from "../store/OverviewContext";
import { Navigate } from "react-router-dom";
import DoorsClosed from "./DoorsClosed";

import BuyNowTop from "./BuyNow/Top";
import BuyNowFAQs from "./BuyNow/FAQs";
import BuyNowGuarantee from "./BuyNow/Guarantee";
import TrialsDisabled from "./FreeTrial/TrialsDisabled";
import {
  MEMBER_STATUS_STANDARD,
  MEMBER_STATUS_TRIAL,
} from "../api/entities/UserProfile";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS SURFACE.** This is the router for the whole /free-trial
 * funnel: which of the four doors/trials states a visitor sees. The conversion
 * replaces one `makeStyles` class on the wrapper `<div>` with a `styled` host
 * element — every `overview.doors` condition, `<Seo>` prop, child component and
 * the Firebase analytics effect are byte-identical.
 */

// was: useStyles().container — a bare <div>, which has no `sx` prop.
const PageContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
}));

// #1264: `platform` (the ad route: fb/sc/tt/ga) no longer changes the page.
// It picked the install block's smart.link campaign variant, and the website
// no longer uses smart.link. The routes still pass it (Routes.js).
const FreeTrialLanding = (_props: { platform: string | undefined }) => {
  const { loggedIn, userProfile } = useContext(AccountContext);
  const { overview } = useContext(OverviewContext);

  useEffect(() => {
    logEventFirebase(getAnalytics(firebaseApp), "seen_landing_page_1");
    setUserProperties(getAnalytics(firebaseApp), {
      seen_landing_page_1: "1",
    });
  }, []);

  return (
    <>
      {/* #1245: only members and trials leave for /videos, as /try does
          (Onboarding/Public.tsx). The sign-up signs a new free account in
          before its last step, so sending every signed-in visitor away would
          skip "Get the app". */}
      {loggedIn &&
      (userProfile?.memberStatus === MEMBER_STATUS_STANDARD ||
        userProfile?.memberStatus === MEMBER_STATUS_TRIAL) ? (
        <Navigate to="/videos" replace />
      ) : null}

      {overview && (
        /*
            Doors Open & Trials Open - show Free Trial
            Doors Closed & Trials Open - show Free Trial
            Doors Open & Trials Closed - show Join Now
            Doors Closed & Trials Closed - show Free Trial (notify me status)
          */
        <>
          {overview.doors?.trialsOpen &&
            overview.doors?.open &&
            overview.doors?.trialSMSEnabled && (
              <>
                <Seo
                  title="Take a 72 Hour Free Trial Now!"
                  description="Now is your opportunity to take a 72 Hour Free Trial."
                  path="/free-trial"
                />
                <PageContainer>
                  <Top />
                  <Stepper />
                  <Video />
                  <WhatIs />
                  <Testimonials />
                  <FAQs />
                  <Trial />
                  <InstallApp layout="footer" />
                </PageContainer>
              </>
            )}

          {overview.doors?.trialsOpen &&
            !overview.doors?.open &&
            overview.doors?.trialSMSEnabled && (
              <>
                <Seo
                  title="Take a 72 Hour Free Trial Now!"
                  description="Now is your opportunity to take a 72 Hour Free Trial."
                  path="/free-trial"
                />
                <PageContainer>
                  <Top />
                  <Stepper />
                  <Video />
                  <WhatIs />
                  <Testimonials />
                  <FAQs />
                  <Trial />
                  <InstallApp layout="footer" />
                </PageContainer>
              </>
            )}

          {overview.doors?.open && !overview.doors?.trialsOpen && (
            <>
              {" "}
              <Seo
                title="Join The Clan Now!"
                description="Last chance to get a FarmFLiX Membership this year!."
                path="/free-trial"
              />
              <PageContainer>
                <BuyNowTop />
                <Video />
                <WhatIs />
                <Testimonials />
                <BuyNowFAQs />
                <BuyNowGuarantee />
              </PageContainer>
            </>
          )}

          {!overview.doors?.trialsOpen && !overview.doors?.open && (
            <>
              <DoorsClosed />
            </>
          )}

          {!overview.doors?.trialSMSEnabled && (
            <>
              <TrialsDisabled />
            </>
          )}

          {overview.doors?.trialsOpen &&
            !overview.doors?.open &&
            !overview.doors?.trialSMSEnabled && (
              <>
                <TrialsDisabled />
              </>
            )}
        </>
      )}
    </>
  );
};

export default FreeTrialLanding;
